import React, { useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { Button } from '@/components/ui/button'
import { AlertCircle, Clock } from 'lucide-react'
import { SendDecisionModal } from '@/components/Student/SendDecisionModal'
import { submitStudentDecision } from '@/redux/asyncThunks/studentDecisionWebsocket'
import { useGamePhase } from '@/hooks/useGamePhase'
import { findUserDecision, findTeamDecision } from '@/lib/utils'
import { ActivityState } from '@/redux/slices/timerConfigurationSlice'
import { TimerDisplay } from '../settings/timer-configuration/TimerDisplay'
import { useEffectiveUser } from '@/hooks/useEffectiveUser'
import { useTeamCommunicator } from '@/hooks/useTeamCommunicator'
import { TimerMode } from '@/components/settings/timer-configuration/types'
import {
  researchOptionsKey,
  ResearchDecisionOptionsPayload,
  ResearchOptionsStatus,
  setResearchDecisionOptions,
} from '@/redux/slices/researchModeSlice'
import { getResearchDecisionOptionsForPhase } from '@/api/researchMode'
import { usePendingSubmission } from '@/hooks/usePendingSubmission'

interface DecisionSectionProps {
  phaseId: number | null
}

const EXPECTED_RESEARCH_OPTIONS_COUNT = 3

type ResearchOptionsInput = {
  phase_id: number
  student_id?: number | null
  options?: unknown
  status?: unknown
  error_message?: string | null
}

const normalizeResearchDecisionOptions = (
  input: ResearchOptionsInput,
  fallbackStudentId: number
): ResearchDecisionOptionsPayload => {
  const status =
    input.status === ResearchOptionsStatus.PENDING ||
    input.status === ResearchOptionsStatus.READY ||
    input.status === ResearchOptionsStatus.FAILED
      ? (input.status as ResearchOptionsStatus)
      : ResearchOptionsStatus.FAILED
  const options = Array.isArray(input.options)
    ? input.options.filter((option): option is string => typeof option === 'string')
    : []
  const basePayload: ResearchDecisionOptionsPayload = {
    phase_id: input.phase_id,
    student_id: input.student_id ?? fallbackStudentId,
    options,
    status,
    error_message: input.error_message ?? null,
  }

  if (
    basePayload.status === 'ready' &&
    basePayload.options.length !== EXPECTED_RESEARCH_OPTIONS_COUNT
  ) {
    return {
      ...basePayload,
      status: ResearchOptionsStatus.FAILED,
      options: [],
      error_message:
        'Received an invalid set of decision options. Please try again.',
    }
  }

  return basePayload
}

const DecisionSection: React.FC<DecisionSectionProps> = () => {
  const dispatch = useAppDispatch()
  const { user } = useEffectiveUser()
  const { decisions } = useAppSelector((state) => state.decisionsWS)
  const [showDecisionModal, setShowDecisionModal] = useState(false)
  const {
    phaseId,
    phaseStatus,
    isInfinitePhase,
    roundStatus,
    endTime,
    isDecisionPhase,
  } = useGamePhase()

  const currentRoomId = useAppSelector(
    (state) => state.room.currentRoom?.roomid
  )
  const gameMode = useAppSelector((state) => state.room.currentRoom?.game_mode)
  const isResearchRoom = gameMode === TimerMode.RESEARCH_MODE
  const researchOptionsKeyStr = useMemo(() => {
    if (phaseId == null || user?.id == null) return null
    return researchOptionsKey(phaseId, user.id)
  }, [phaseId, user?.id])

  const researchOptionsState = useAppSelector((state) =>
    researchOptionsKeyStr
      ? state.researchMode.optionsByKey[researchOptionsKeyStr]
      : undefined
  )
  const roomMembers = useAppSelector((state) => state.room.roomMembers)
  const { winningStrategyText, votingClosed } = useAppSelector(
    (state) => state.teamDecision
  )

  const {
    isTeamCommunicator,
    isLoading: isLoadingCommunicator,
    teamCommunicator,
    isTeamMode,
  } = useTeamCommunicator()

  const communicatorUserId = useMemo(() => {
    if (!teamCommunicator?.communicator_email) return undefined
    const member = roomMembers.find(
      (m) => m.member_email === teamCommunicator.communicator_email
    )
    return member?.member_id ? Number(member.member_id) : undefined
  }, [teamCommunicator?.communicator_email, roomMembers])

  const userTeamId = useMemo(() => {
    if (!isTeamMode || !user?.email) return null
    const member = roomMembers.find((m) => m.member_email === user.email)
    return member?.teamId || null
  }, [isTeamMode, user?.email, roomMembers])

  const myDecision = useMemo(() => {
    if (isTeamMode && isDecisionPhase && communicatorUserId) {
      const teamDecision = findTeamDecision(
        decisions,
        communicatorUserId,
        phaseId
      )
      if (teamDecision) {
        return teamDecision
      }
    }
    return findUserDecision(decisions, user?.id, phaseId)
  }, [
    decisions,
    user?.id,
    phaseId,
    isTeamMode,
    isDecisionPhase,
    communicatorUserId,
  ])

  const isTeamDecision = useMemo(() => {
    if (
      !isTeamMode ||
      !isDecisionPhase ||
      !teamCommunicator ||
      !myDecision ||
      !communicatorUserId
    ) {
      return false
    }
    return myDecision.student_id === communicatorUserId
  }, [
    isTeamMode,
    isDecisionPhase,
    teamCommunicator,
    myDecision,
    communicatorUserId,
  ])

  const decisionExists = !!myDecision?.decision
  // Sent, but not back from the server yet: no second submission meanwhile.
  const submission = usePendingSubmission(decisionExists)
  const isLateSubmission = myDecision?.late_submission

  const handleSubmitDecision = (
    decision: string,
    isLate: boolean,
    isPublic: boolean,
    research?: { index: number; justification: string }
  ) => {
    if (!currentRoomId || !user?.id) return

    if (phaseId !== null) {
      submission.start()
      dispatch(
        submitStudentDecision({
          studentId: user.id,
          roomId: currentRoomId,
          decision,
          lateSubmission: isLate,
          phaseId: phaseId,
          isPublic: isPublic,
          teamId: isTeamMode ? userTeamId : null,
          selectedResearchOptionIndex:
            isResearchRoom && research ? research.index : undefined,
          decisionJustification:
            isResearchRoom && research ? research.justification : undefined,
        })
      )
        .unwrap()
        .catch(submission.clear)
    }
  }

  const fetchResearchDecisionOptions = React.useCallback(() => {
    if (!isResearchRoom || phaseId == null || !currentRoomId || user?.id == null) {
      return
    }
    dispatch(
      setResearchDecisionOptions({
        phase_id: phaseId,
        student_id: user.id,
        options: [],
        status: ResearchOptionsStatus.PENDING,
        error_message: null,
      })
    )
    void getResearchDecisionOptionsForPhase(currentRoomId, phaseId)
      .then((data) => {
        dispatch(
          setResearchDecisionOptions(
            normalizeResearchDecisionOptions(data, user.id)
          )
        )
      })
      .catch((error: Error) => {
        dispatch(
          setResearchDecisionOptions({
            phase_id: phaseId,
            student_id: user.id,
            options: [],
            status: ResearchOptionsStatus.FAILED,
            error_message:
              error.message ||
              'Could not load decision options. Please try again.',
          })
        )
      })
  }, [currentRoomId, dispatch, isResearchRoom, phaseId, user?.id])

  useEffect(() => {
    if (
      !showDecisionModal ||
      !isResearchRoom ||
      phaseId == null ||
      !currentRoomId ||
      user?.id == null
    ) {
      return
    }
    fetchResearchDecisionOptions()
  }, [showDecisionModal, isResearchRoom, phaseId, currentRoomId, user?.id, fetchResearchDecisionOptions])

  const statusMessage = useMemo(() => {
    if (isTeamMode && isDecisionPhase && !isLoadingCommunicator) {
      if (isTeamCommunicator) {
        return {
          type: 'info',
          text: 'You are the team communicator. Only you can submit the team decision.',
        }
      } else {
        return {
          type: 'waiting',
          text: `Waiting for ${teamCommunicator?.communicator_name || 'team communicator'} to submit the team decision.`,
        }
      }
    }
    return null
  }, [
    isTeamMode,
    isDecisionPhase,
    isLoadingCommunicator,
    isTeamCommunicator,
    teamCommunicator,
  ])

  const isTimerEnded = roundStatus === ActivityState.Completed

  const shouldShowWaitingMessage = useMemo(
    () =>
      isTeamMode &&
      isDecisionPhase &&
      !isTeamCommunicator &&
      !isLoadingCommunicator,
    [isTeamMode, isDecisionPhase, isTeamCommunicator, isLoadingCommunicator]
  )

  const isSubmitDisabled = useMemo(
    () =>
      roundStatus === ActivityState.Paused ||
      (isTeamMode && isDecisionPhase && !isTeamCommunicator),
    [roundStatus, isTeamMode, isDecisionPhase, isTeamCommunicator]
  )

  const prefillDecision = useMemo(() => {
    if (
      isTeamMode &&
      isDecisionPhase &&
      isTeamCommunicator &&
      votingClosed &&
      winningStrategyText
    ) {
      return winningStrategyText
    }
    return undefined
  }, [
    isTeamMode,
    isDecisionPhase,
    isTeamCommunicator,
    votingClosed,
    winningStrategyText,
  ])

  return (
    <div className='px-4 py-6 my-4 bg-white border rounded-md shadow-sm'>
      <div className='mb-4 flex items-center justify-between'>
        <h2 className='text-lg font-semibold'>Decision Submission</h2>
        {statusMessage && isTimerEnded && (
          <div className='flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700'>
            <Clock className='h-3 w-3' />
            <span>Timer Ended</span>
          </div>
        )}
        {!statusMessage && isTimerEnded && (
          <div className='flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700'>
            <Clock className='h-3 w-3' />
            <span>Timer Ended</span>
          </div>
        )}
      </div>

      {statusMessage && (
        <div className='mb-4 text-sm text-gray-600'>
          {statusMessage.text}
          {isTeamMode && (
            <span className='text-gray-500'>
              {' '}
              Discuss with your team in the Team Room.
            </span>
          )}
        </div>
      )}

      {decisionExists ? (
        <div className='space-y-3'>
          <div className='p-4 rounded-md bg-gray-50'>
            <h3 className='mb-2 font-medium'>
              {isTeamDecision
                ? 'Team Decision (Submitted by Team Communicator):'
                : 'Your Decision:'}
            </h3>
            <p className='whitespace-pre-wrap'>{myDecision?.decision}</p>
            {isResearchRoom && myDecision?.decision_justification && (
              <div className='mt-3 border-t border-gray-200 pt-3'>
                <h4 className='mb-1 text-sm font-medium text-gray-700'>
                  Justification
                </h4>
                <p className='whitespace-pre-wrap text-sm text-gray-800'>
                  {myDecision.decision_justification}
                </p>
              </div>
            )}
            {isTeamDecision && teamCommunicator && (
              <p className='mt-2 text-sm text-gray-600'>
                Submitted by: {teamCommunicator.communicator_name} (
                {teamCommunicator.communicator_email})
              </p>
            )}
          </div>

          {isLateSubmission && (
            <div className='flex items-center gap-2 text-sm text-amber-700'>
              <AlertCircle className='h-4 w-4' />
              <span>This decision was submitted after the timer ended.</span>
            </div>
          )}

          {myDecision?.adjudication && (
            <div className='p-4 mt-3 border border-blue-200 rounded-md bg-blue-50'>
              <h3 className='mb-2 font-medium text-blue-800'>Adjudication:</h3>
              <p className='text-blue-700 whitespace-pre-wrap'>
                {myDecision.adjudication}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className='space-y-3'>
          {shouldShowWaitingMessage ? (
            <div className='space-y-3'>
              {votingClosed && winningStrategyText && (
                <div className='rounded-lg border border-amber-200 bg-amber-50 p-4'>
                  <h3 className='mb-2 font-medium text-amber-800'>
                    Team&apos;s chosen strategy (from voting):
                  </h3>
                  <p className='text-sm text-amber-900 whitespace-pre-wrap'>
                    {winningStrategyText}
                  </p>
                </div>
              )}
              <div className='rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-600'>
                The team decision will appear here once the team communicator
                submits it.
              </div>
            </div>
          ) : (
            <div className='flex items-center justify-between gap-4'>
              <div className='flex-1'>
                <Button
                  disabled={isSubmitDisabled || submission.pending}
                  onClick={() => setShowDecisionModal(true)}
                  className={
                    roundStatus === ActivityState.Completed
                      ? 'bg-amber-600 hover:bg-amber-700'
                      : 'bg-dark-blue hover:bg-dark-blue-hovered'
                  }
                >
                  {submission.pending
                    ? 'Submitting…'
                    : roundStatus === ActivityState.Completed
                      ? 'Submit Late Decision'
                      : 'Submit Decision'}
                </Button>
                {isTimerEnded && (
                  <p className='mt-2 text-xs text-amber-600'>
                    Submissions will be marked as late
                  </p>
                )}
              </div>

              <div className='flex items-center text-sm'>
                <TimerDisplay
                  roundStatus={roundStatus}
                  isInfinitePhase={isInfinitePhase}
                  endTime={endTime}
                  phaseStatus={phaseStatus}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <SendDecisionModal
        isOpen={showDecisionModal}
        onClose={() => setShowDecisionModal(false)}
        onSend={handleSubmitDecision}
        adjudication={myDecision?.adjudication || ''}
        finalDecision={myDecision?.decision || ''}
        prefillDecision={prefillDecision}
        researchMode={isResearchRoom}
        researchOptions={researchOptionsState?.options || []}
        researchStatus={researchOptionsState?.status || ''}
        researchError={researchOptionsState?.error_message ?? null}
        onRetryResearchOptions={fetchResearchDecisionOptions}
        phaseId={phaseId}
      />
    </div>
  )
}

export default DecisionSection
